import { useState } from 'react'
import { MessageSquare, Plus, Trash2 } from 'lucide-react'

interface Session {
  id: string
  title: string | null
  scope: string
  message_count: number
  last_message_at: string | null
  created_at: string
}

interface Props {
  sessions: Session[]
  currentSessionId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  onClose: () => void
  onDelete: (id: string) => void
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin === 1) return '1 min ago'
  if (diffMin < 60) return `${diffMin} min ago`
  if (diffHr === 1) return '1 hour ago'
  if (diffHr < 24) return `${diffHr} hours ago`
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay} days ago`
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} weeks ago`
  return date.toLocaleDateString()
}

export function ChatSessionList({ sessions, currentSessionId, onSelect, onNew, onClose, onDelete }: Props) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Chat History</h3>
        <button onClick={onClose} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Back</button>
      </div>

      <div className="px-3 py-2">
        <button
          onClick={onNew}
          className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors font-medium"
        >
          <Plus className="w-4 h-4" />
          New Chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 space-y-0.5">
        {sessions.map(s => {
          const isActive = s.id === currentSessionId
          const isDeleting = deletingId === s.id
          const timeStr = s.last_message_at
            ? formatRelativeTime(s.last_message_at)
            : formatRelativeTime(s.created_at)

          if (isDeleting) {
            return (
              <div
                key={s.id}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-red-50 text-sm"
              >
                <span className="text-red-700 text-xs font-medium">Delete this chat?</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onDelete(s.id); setDeletingId(null) }}
                    className="text-xs font-medium text-red-600 hover:text-red-700"
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setDeletingId(null)}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    No
                  </button>
                </div>
              </div>
            )
          }

          return (
            <button
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg group relative flex items-start gap-2.5 transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
              style={isActive ? { borderLeft: '3px solid #6d28d9' } : undefined}
            >
              <MessageSquare className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isActive ? '' : 'text-gray-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.title || 'Untitled'}</div>
                <div className={`text-[11px] mt-0.5 ${isActive ? 'text-primary-500' : 'text-gray-400'}`}>
                  {s.message_count} messages · {timeStr}
                </div>
              </div>
              {!isActive && (
                <button
                  onClick={(e) => { e.stopPropagation(); setDeletingId(s.id) }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all absolute right-2 top-2.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </button>
          )
        })}

        {sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-12">
            <MessageSquare className="w-10 h-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-400">Start your first conversation</p>
          </div>
        )}
      </div>
    </div>
  )
}
